import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { campaignApi } from '@/api/feedback/campaignApi';
import { type Question, QuestionType } from '@/models/feedback/Campaign';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    brandId: string;
    question: Question | null; // null = create
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const QuestionDialog = ({ open, brandId, question, readOnly, onClose }: Props) => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const [name, setName] = useState('');
    const [questionType, setQuestionType] = useState<QuestionType>(QuestionType.RatingScale);
    const [trigger, setTrigger] = useState<number>(0);

    useEffect(() => {
        if (!open) return;
        setName(question?.name ?? '');
        setQuestionType(question?.questionType ?? QuestionType.RatingScale);
        setTrigger(question?.trigger ?? 0);
    }, [open, question]);

    // A free-text answer has no rating, so no threshold can apply to it. The field is hidden rather than
    // disabled, and 0 is sent, because a visible-but-dead alert threshold reads as a rule that is in force.
    const isFreeText = questionType === QuestionType.FreeText;

    const { mutateAsync: save, isPending } = useMutation({
        mutationFn: async () => {
            const payload = { name, questionType, trigger: isFreeText ? 0 : trigger };
            if (question) {
                await campaignApi.updateQuestion(question.id, payload);
            } else {
                await campaignApi.createQuestion({ brandId, ...payload });
            }
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.feedbackQuestionSaved });
            await qc.invalidateQueries({ queryKey: ['feedback-questions', brandId] });
            onClose();
        },
        onError: () => toast({ severity: 'error', message: strings.feedbackQuestionSaveError }),
    });

    const canSave = name.trim().length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{question ? strings.feedbackEditQuestion : strings.feedbackNewQuestion}</DialogTitle>
            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label={strings.feedbackQuestion}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        select
                        label={strings.feedbackQuestionType}
                        value={questionType}
                        onChange={(e) => setQuestionType(Number(e.target.value) as QuestionType)}
                        fullWidth
                    >
                        <MenuItem value={QuestionType.RatingScale}>{strings.feedbackQuestionTypeRatingScale}</MenuItem>
                        <MenuItem value={QuestionType.FreeText}>{strings.feedbackQuestionTypeFreeText}</MenuItem>
                    </TextField>
                    {isFreeText ? (
                        <FormHelperText>{strings.feedbackFreeTextHelp}</FormHelperText>
                    ) : (
                        <TextField
                            label={strings.feedbackTriggerHelp}
                            type="number"
                            value={trigger === 0 ? '' : trigger}
                            onChange={(e) => setTrigger(Number(e.target.value) || 0)}
                            fullWidth
                        />
                    )}
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" loading={isPending} disabled={!canSave || isPending} onClick={() => save()}>{strings.save}</Button>}
            </DialogActions>
        </Dialog>
    );
};

export default QuestionDialog;
